import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Button,
  Form,
  Label,
  Col,
  Row,
  Input,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import validator from "validator";
import {
  addCandidatesSkills,
  getCountries,
  getCityCountry,
  myCandiDetailsShortlistedRole,
  RoleBasedSkill,
  addCandidatesLocation,
} from "../PartnerSlice";
import { dateyyyymmdd_tohtml } from "../../helpers/date";
import { log_debug } from "../../apiHelpers/logger";

const CandidateEditProfile = ({ handleProfileSave, data }) => {
  const dispatch = useDispatch();
  const partnerStore = useSelector((state) => state.partnerSlice);

  // State management
  const [errors, setErrors] = useState({});
  const [editedData, setEditedData] = useState(data || {});
  const [allSkills, setAllSkills] = useState([]);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [enumcities, setEnumCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState({ value: "", label: "" });
  const [selectedCities, setSelectedCities] = useState([]);
  const [MyCandidateDetailsRoleDropdown, setMyCandidateDetailsRoleDropdown] = useState([]);
  
  // Form fields
  const [formData, setFormData] = useState({
    rate: "",
    totalExperience: "",
    relevantExperience: "",
    noticePeriod: "",
    availability: "",
    linkedinurl: "",
    dateofbirth: "",
  });

  // Memoized options
  const roleoptions = useMemo(() => 
    MyCandidateDetailsRoleDropdown.map((item) => ({
      id: item._id,
      value: item.code,
      label: item.title,
    })), [MyCandidateDetailsRoleDropdown]
  );

  const allCountry = useMemo(() => {
    if (partnerStore?.data?.Countries?.success) {
      return partnerStore.data.Countries.data.enumcountrys.map((country) => ({
        id: country._id,
        label: country.title,
        value: country.code,
      }));
    }
    return [];
  }, [partnerStore?.data?.Countries]);

  const allCity = useMemo(() => {
    if (partnerStore?.data?.countryCities?.success) {
      return partnerStore.data.countryCities.data.enumcitys.map((city) => ({
        id: city._id,
        label: city.title,
        value: city.code,
      }));
    }
    return [];
  }, [partnerStore?.data?.countryCities]);

  // Initialize form data
  useEffect(() => {
    if (data) {
      setEditedData(data);
      
      // Set skills
      if (data.skills) {
        const tempSkill = data.skills.map((skill) => ({
          label: skill,
          value: skill,
        }));
        setSelectedSkills(tempSkill);
      }

      // Set form data
      setFormData({
        rate: data.monthly_salary || "",
        totalExperience: data.experience || "",
        relevantExperience: data.relevant_experience || "",
        noticePeriod: data.notice_period || "",
        availability: data.availability || "",
        linkedinurl: data.linkedin_url || "",
        dateofbirth: data.date_of_birth || "",
      });

      // Set locations
      if (data.current_location) {
        setSelectedCity({
          value: data.current_location.toLowerCase(),
          label: data.current_location,
        });
      }

      if (data.prefered_locations) {
        const preferredLocation = data.prefered_locations.map((location) => ({
          value: location.toLowerCase(),
          label: location,
        }));
        setSelectedCities(preferredLocation);
      }
    }
  }, [data]);

  // Initialize data on component mount
  useEffect(() => {
    const initializeData = async () => {
      dispatch(myCandiDetailsShortlistedRole());
      dispatch(addCandidatesSkills());
      dispatch(getCountries());
      
      const res = await dispatch(addCandidatesLocation());
      if (res?.payload?.data?.enumcitys) {
        const cities = res.payload.data.enumcitys.map((item) => ({
          value: item.code,
          label: item.title,
        }));
        setEnumCities(cities);
      }
    };
    
    initializeData();
  }, [dispatch]);

  // Handle role-based skills
  useEffect(() => {
    if (editedData?.role) {
      dispatch(RoleBasedSkill(editedData.role));
    }
  }, [editedData?.role, dispatch]);

  // Handle country-based cities
  useEffect(() => {
    if (editedData?.address?.country) {
      dispatch(getCityCountry(editedData.address.country));
    }
  }, [editedData?.address?.country, dispatch]);

  // Update state when store changes
  useEffect(() => {
    if (partnerStore.status === "idle") {
      // Role dropdown
      if (partnerStore.data.myCandiDetailsShortlistedRoleRecords?.success) {
        setMyCandidateDetailsRoleDropdown(
          partnerStore.data.myCandiDetailsShortlistedRoleRecords.data.enumjobroles
        );
      }

      // Skills
      if (partnerStore.data?.CandidatesSkills?.success) {
        const tempSkill = partnerStore.data.CandidatesSkills.data.enumskills.map((skill) => ({
          label: skill.title,
          value: skill.code,
        }));
        setAllSkills(tempSkill);
      }

      // Role-based skills
      if (partnerStore.data?.RoleBasedSkill?.success && editedData?.role) {
        const tempSkill = partnerStore.data.RoleBasedSkill.data.skills.map((skill) => ({
          label: skill,
          value: skill,
        }));
        setSelectedSkills(tempSkill);
        setEditedData(prev => ({ ...prev, skills: tempSkill.map((sk) => sk.label) }));
      }
    }
  }, [partnerStore, editedData?.role]);

  // Validation function
  const validateForm = useCallback(() => {
    const today = new Date();
    const todayFormatted = today.toISOString().split("T")[0];
    let newErrors = {};

    // Name validation
    if (validator.isEmpty(editedData?.full_name || "")) {
      newErrors.full_name = "Name is required";
    } else if (
      !validator.isLength(editedData.full_name, { min: 3, max: 40 }) ||
      !validator.isAlpha(editedData.full_name, ["en-IN"], { ignore: " ." })
    ) {
      newErrors.full_name = "Please enter a valid name";
    }

    // Email validation
    if (!editedData?.email || validator.isEmpty(editedData.email)) {
      newErrors.email = "Email is required";
    } else if (!validator.isEmail(editedData.email)) {
      newErrors.email = "Please enter valid email";
    } else if (!validator.isLength(editedData.email, { min: 0, max: 50 })) {
      newErrors.email = "Email is too large";
    }

    // Phone validation
    if (!editedData?.contact_number || validator.isEmpty(editedData.contact_number)) {
      newErrors.contact_number = "Phone is required";
    } else if (!validator.isMobilePhone(editedData.contact_number)) {
      newErrors.contact_number = "Please enter valid phone";
    }

    // Address validation
    if (!editedData?.address?.city || validator.isEmpty(editedData.address.city)) {
      newErrors["address.city"] = "City is required";
    }
    if (!editedData?.address?.country || validator.isEmpty(editedData.address.country)) {
      newErrors["address.country"] = "Country is required";
    }

    // Role and skills validation
    if (!editedData?.role) {
      newErrors.role = "Role is required";
    }
    if (!editedData?.skills?.length) {
      newErrors.skills = "Skill is required";
    }

    // Summary validation
    if (validator.isEmpty(editedData?.summary || "")) {
      newErrors.summary = "Summary is required";
    } else if (!validator.isLength(editedData.summary, { min: 0, max: 1000 })) {
      newErrors.summary = "Summary is too large";
    }

    // Date of birth validation
    if (!formData.dateofbirth || validator.isEmpty(formData.dateofbirth)) {
      newErrors.date_of_birth = "Date of birth is required";
    } else if (validator.isAfter(formData.dateofbirth, { comparisonDate: todayFormatted })) {
      newErrors.date_of_birth = "Date of Birth cannot be greater than today";
    }

    // Experience validation
    if (!formData.totalExperience || validator.isEmpty(formData.totalExperience.toString())) {
      newErrors.totalexperience = "Total Experience is required";
    }
    if (!formData.relevantExperience || validator.isEmpty(formData.relevantExperience.toString())) {
      newErrors.relevantexperience = "Relevant Experience is required";
    }

    // Location validation
    if (validator.isEmpty(selectedCity.label || "")) {
      newErrors.currentlocation = "Current Location is required";
    }
    if (!selectedCities.length) {
      newErrors.preferedlocation = "Preferred Location is required";
    }

    // Other field validations
    if (!formData.rate || validator.isEmpty(formData.rate.toString())) {
      newErrors.rate = "Rate is required";
    }
    if (validator.isEmpty(formData.noticePeriod.toString())) {
      newErrors.noticeperiod = "Notice Period is required";
    }
    if (!formData.availability || validator.isEmpty(formData.availability)) {
      newErrors.availability = "Availability is required";
    }
    if (!formData.linkedinurl || validator.isEmpty(formData.linkedinurl)) {
      newErrors.linkedin = "LinkedIn URL is required";
    } else if (!validator.isLength(formData.linkedinurl, { min: 0, max: 255 })) {
      newErrors.linkedin = "LinkedIn ID is too large";
    }

    return newErrors;
  }, [editedData, formData, selectedCity, selectedCities]);

  // Handle form submission
  const handleValidateSave = useCallback(() => {
    const newErrors = validateForm();
    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      const finalData = {
        ...editedData,
        monthly_salary: Number(formData.rate) || 0,
        experience: Number(formData.totalExperience),
        relevant_experience: Number(formData.relevantExperience),
        prefered_locations: selectedCities.map((city) => city.label),
        current_location: selectedCity.label,
        notice_period: formData.noticePeriod,
        linkedin_url: formData.linkedinurl,
        date_of_birth: formData.dateofbirth,
        availability: formData.availability,
      };
      handleProfileSave(finalData);
    }
  }, [validateForm, editedData, formData, selectedCity, selectedCities, handleProfileSave]);

  // Generic change handler
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    
    // Clear error for this field
    setErrors(prev => ({ ...prev, [name]: "" }));

    if (name === "address.street") {
      setEditedData(prev => ({
        ...prev,
        address: { ...prev.address, street: value },
      }));
    } else if (name.includes(".")) {
      const [objectName, propertyName] = name.split(".");
      setEditedData(prev => ({
        ...prev,
        [objectName]: { ...prev[objectName], [propertyName]: value },
      }));
    } else {
      setEditedData(prev => ({ ...prev, [name]: value }));
    }
  }, []);

  // Form data change handler
  // Form data change handler - FIXED VERSION
const handleFormDataChange = useCallback((field, value) => {
  // Map field names to their corresponding error keys
  const errorKeyMap = {
    totalExperience: "totalexperience",
    relevantExperience: "relevantexperience",
    rate: "rate",
    noticePeriod: "noticeperiod",
    availability: "availability",
    linkedinurl: "linkedin",
    dateofbirth: "date_of_birth"
  };
  
  // Clear the corresponding error
  const errorKey = errorKeyMap[field] || field;
  setErrors(prev => ({ ...prev, [errorKey]: "" }));
  
  // Update form data
  setFormData(prev => ({ ...prev, [field]: value }));
}, []);

  // Skills handler
  const handleSkills = useCallback((skill) => {
    setEditedData(prev => ({ ...prev, skills: skill.map((sk) => sk.label) }));
    setSelectedSkills(skill);
  }, []);

  return (
    <Form>
      <Row className="mb-3">
        <Label htmlFor="fullName" className="col-sm-3 col-form-label">
          Full Name <span className="text-danger">*</span>
        </Label>
        <Col sm={9}>
          <Input
            type="text"
            id="fullName"
            placeholder="Enter full name"
            name="full_name"
            value={editedData.full_name || ""}
            onChange={handleChange}
          />
          {errors.full_name && (
            <small className="text-danger">{errors.full_name}</small>
          )}
        </Col>
      </Row>

      <Row className="mb-3">
        <Label htmlFor="contactNo" className="col-sm-3 col-form-label">
          Contact No <span className="text-danger">*</span>
        </Label>
        <Col sm={9}>
          <Input
            type="text"
            id="contactNo"
            placeholder="Enter contact number"
            name="contact_number"
            value={editedData.contact_number || ""}
            onChange={handleChange}
          />
          {errors.contact_number && (
            <small className="text-danger">{errors.contact_number}</small>
          )}
        </Col>
      </Row>

      <Row className="mb-3">
        <Label htmlFor="email" className="col-sm-3 col-form-label">
          Email <span className="text-danger">*</span>
        </Label>
        <Col sm={9}>
          <Input
            type="email"
            id="email"
            placeholder="Enter your Email"
            name="email"
            value={editedData.email || ""}
            onChange={handleChange}
          />
          {errors.email && (
            <small className="text-danger">{errors.email}</small>
          )}
        </Col>
      </Row>

      <Row className="mb-3">
        <Label htmlFor="role" className="col-sm-3 col-form-label">
          Professional Role <span className="text-danger">*</span>
        </Label>
        <Col sm={9}>
          <select
            className="form-select"
            id="role"
            name="role"
            onChange={handleChange}
            value={editedData.role || ""}
          >
            <option value="" disabled>
              Select Role
            </option>
            {roleoptions.map((role) => (
              <option key={role.id} value={role.label}>
                {role.label}
              </option>
            ))}
          </select>
          {errors.role && (
            <small className="text-danger">{errors.role}</small>
          )}
        </Col>
      </Row>

      <Row className="mb-3">
        <Label htmlFor="skills" className="col-sm-3 col-form-label">
          Skills <span className="text-danger">*</span>
        </Label>
        <Col sm={9}>
          <Select
            className="basic-multi-select"
            name="skills"
            placeholder="Select Skills"
            options={allSkills}
            onChange={handleSkills}
            value={selectedSkills}
            isMulti
            isSearchable
            isOptionDisabled={() => selectedSkills.length > 6}
            components={makeAnimated()}
          />
          {errors.skills && (
            <small className="text-danger">{errors.skills}</small>
          )}
        </Col>
      </Row>

      <Row className="mb-3">
        <Label className="col-sm-3 col-form-label" htmlFor="address">
          Address <span className="text-danger">*</span>
        </Label>
        <Col sm={9}>
          <Row className="mb-3">
            <Col sm={12}>
              <Input
                type="text"
                id="street"
                placeholder="Enter street"
                name="address.street"
                value={editedData?.address?.street || ""}
                onChange={handleChange}
              />
            </Col>
          </Row>
          <Row>
            <Col sm={6}>
              <select
                className="form-select"
                name="address.country"
                onChange={handleChange}
                value={editedData?.address?.country || ""}
              >
                <option value="" disabled>
                  Select Country
                </option>
                {allCountry.map((country) => (
                  <option key={country.id} value={country.label}>
                    {country.label}
                  </option>
                ))}
              </select>
              {errors["address.country"] && (
                <small className="text-danger">{errors["address.country"]}</small>
              )}
            </Col>
            <Col sm={6}>
              <select
                className="form-select"
                name="address.city"
                onChange={handleChange}
                value={editedData?.address?.city || ""}
              >
                <option value="" disabled>
                  Select City
                </option>
                {allCity.map((city) => (
                  <option key={city.id} value={city.label}>
                    {city.label}
                  </option>
                ))}
              </select>
              {errors["address.city"] && (
                <small className="text-danger">{errors["address.city"]}</small>
              )}
            </Col>
          </Row>
        </Col>
      </Row>

      <Row className="mb-3">
        <Label htmlFor="total_experience" className="col-sm-3 col-form-label">
          Total Experience
        </Label>
        <Col sm={3}>
          <Input
            type="number"
            min={0}
            max={50}
            onChange={(e) => handleFormDataChange("totalExperience", e.target.value)}
            value={formData.totalExperience}
            placeholder="Enter Total Experience"
          />
          {errors.totalexperience && (
            <small className="text-danger">{errors.totalexperience}</small>
          )}
        </Col>

        <Label htmlFor="relevant_experience" className="col-sm-3 col-form-label text-end">
          Relevant Experience
        </Label>
        <Col sm={3}>
          <Input
            type="number"
            min={0}
            max={50}
            onChange={(e) => handleFormDataChange("relevantExperience", e.target.value)}
            value={formData.relevantExperience}
            placeholder="Enter Relevant Experience"
          />
          {errors.relevantexperience && (
            <small className="text-danger">{errors.relevantexperience}</small>
          )}
        </Col>
      </Row>

      <Row className="mb-3">
        <Label htmlFor="current_location" className="col-sm-3 col-form-label">
          Current Location
        </Label>
        <Col sm={3}>
          <Select
            options={enumcities}
            isMulti={false}
            isSearchable
            onChange={(selected) => {
              setErrors(prev => ({ ...prev, currentlocation: "" }));
              setSelectedCity(selected || { value: "", label: "" });
            }}
            value={selectedCity}
          />
          {errors.currentlocation && (
            <small className="text-danger">{errors.currentlocation}</small>
          )}
        </Col>

        <Label htmlFor="prefered_location" className="col-sm-3 col-form-label text-end">
          Preferred Location
        </Label>
        <Col sm={3}>
          <Select
            options={enumcities}
            isMulti={true}
            isSearchable
            onChange={(selected) => {
              setErrors(prev => ({ ...prev, preferedlocation: "" }));
              setSelectedCities(selected || []);
            }}
            value={selectedCities}
            isOptionDisabled={() => selectedCities.length >= 5}
          />
          {errors.preferedlocation && (
            <small className="text-danger">{errors.preferedlocation}</small>
          )}
        </Col>
      </Row>

      <Row className="mb-3">
        <Label htmlFor="rate" className="col-sm-3 col-form-label">
          Rate
        </Label>
        <Col sm={3}>
          <Input
            type="number"
            min={0}
            onChange={(e) => handleFormDataChange("rate", e.target.value)}
            value={formData.rate}
          />
          {errors.rate && (
            <small className="text-danger">{errors.rate}</small>
          )}
        </Col>

        <Label htmlFor="notice_period" className="col-sm-3 col-form-label text-end">
          Notice Period (days)
        </Label>
        <Col sm={3}>
          <Input
            type="number"
            min={0}
            max={365}
            onChange={(e) => handleFormDataChange("noticePeriod", e.target.value)}
            value={formData.noticePeriod}
          />
          {errors.noticeperiod && (
            <small className="text-danger">{errors.noticeperiod}</small>
          )}
        </Col>
      </Row>

      <Row className="mb-3">
        <Label htmlFor="availability" className="col-sm-3 col-form-label">
          Availability
        </Label>
        <Col sm={9}>
          <select
            className="form-select"
            id="availability"
            name="availability"
            value={formData.availability}
            onChange={(e) => handleFormDataChange("availability", e.target.value)}
          >
            <option value="" disabled>
              Select Availability
            </option>
            <option value="full-time">Full Time</option>
            <option value="part-time">Part Time</option>
          </select>
          {errors.availability && (
            <small className="text-danger">{errors.availability}</small>
          )}
        </Col>
      </Row>

      <Row className="mb-3">
        <Label htmlFor="date_of_birth" className="col-sm-3 col-form-label">
          Date Of Birth <span className="text-danger">*</span>
        </Label>
        <Col sm={9}>
          <Input
            type="date"
            id="date_of_birth"
            name="date_of_birth"
            value={dateyyyymmdd_tohtml(formData.dateofbirth)}
            onChange={(e) => {
              setErrors(prev => ({ ...prev, date_of_birth: "" }));
              handleFormDataChange("dateofbirth", e.target.value);
            }}
          />
          {errors.date_of_birth && (
            <small className="text-danger">{errors.date_of_birth}</small>
          )}
        </Col>
      </Row>

      <Row className="mb-3">
        <Label htmlFor="linkedin_url" className="col-sm-3 col-form-label">
          LinkedIn URL <span className="text-danger">*</span>
        </Label>
        <Col sm={9}>
          <Input
            type="url"
            id="linkedin_url"
            placeholder="Enter LinkedIn URL"
            name="linkedin_url"
            value={formData.linkedinurl}
            onChange={(e) => handleFormDataChange("linkedinurl", e.target.value)}
          />
          {errors.linkedin && (
            <small className="text-danger">{errors.linkedin}</small>
          )}
        </Col>
      </Row>

      <Row className="mb-4">
        <Label htmlFor="summary" className="col-sm-3 col-form-label">
          Summary <span className="text-danger">*</span>
        </Label>
        <Col sm={9}>
          <textarea
            id="summary"
            name="summary"
            className="form-control"
            rows="4"
            placeholder="Enter Your Summary"
            value={editedData.summary || ""}
            onChange={handleChange}
          />
          {errors.summary && (
            <small className="text-danger">{errors.summary}</small>
          )}
        </Col>
      </Row>

      <Row>
        <Col className="d-flex justify-content-end">
          <Button color="primary" onClick={handleValidateSave}>
            Next
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

export default CandidateEditProfile;